/**
 * A calendar's state: the view, the month or week on screen, the hidden
 * sub-regions and whether cancelled workshops show. The page decides where the
 * choices are stored and passes them in with their setter, then uses `range`
 * to fetch and `hidden` to filter its own workshops before handing them on.
 */
import type { Dispatch, SetStateAction } from 'react';

import { toggleHiddenLabel } from './subregion-filter';
import {
  addDaysUtc,
  DEFAULT_WEEK_STARTS_ON,
  viewerDayUtc,
  type WeekStartsOn,
} from './workshops-calendar-dates';
import { fetchRange } from './workshops-calendar-period';
import type { CalendarView } from './workshopsCalendarTypes';

export const ALL_CALENDAR_VIEWS: CalendarView[] = ['Month', 'Week', 'List'];

export interface CalendarChoices {
  view: CalendarView;
  /** A day in the month or week on screen. */
  anchor: Date;
  hidden: string[];
  showCancelled: boolean;
}

export interface CalendarState extends CalendarChoices {
  /**
   * The days to fetch, `YYYY-MM-DD` with both ends included. Month covers the
   * full month grid and Week the week. List ignores the anchor: it runs from a
   * day before today across 100 days, the most the workshop endpoints serve.
   */
  range: { from: string; to: string };
  setView: (view: CalendarView) => void;
  nav: (dir: 'prev' | 'next') => void;
  toggleSubregion: (label: string) => void;
  setHidden: (labels: string[]) => void;
  toggleCancelled: () => void;
}

/** Where a calendar opens: this month or week, in `initialView` if offered,
 *  nothing hidden and cancelled workshops left out. */
export function initialCalendarChoices({
  views = ALL_CALENDAR_VIEWS,
  initialView = 'Month',
  today = new Date(),
}: {
  views?: CalendarView[];
  initialView?: CalendarView;
  today?: Date;
} = {}): CalendarChoices {
  return {
    view: views.includes(initialView) ? initialView : views[0],
    anchor: viewerDayUtc(today),
    hidden: [],
    showCancelled: false,
  };
}

/** A month at a time in Month view, a week at a time otherwise. */
function navigatedAnchor(view: CalendarView, anchor: Date, dir: 'prev' | 'next'): Date {
  return view === 'Month'
    ? new Date(
        Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + (dir === 'prev' ? -1 : 1), 1),
      )
    : addDaysUtc(anchor, dir === 'prev' ? -7 : 7);
}

export function useCalendarState(
  [choices, setChoices]: [CalendarChoices, Dispatch<SetStateAction<CalendarChoices>>],
  {
    today,
    weekStartsOn = DEFAULT_WEEK_STARTS_ON,
  }: { today?: Date; weekStartsOn?: WeekStartsOn } = {},
): CalendarState {
  return {
    ...choices,
    range: fetchRange(choices.view, choices.anchor, weekStartsOn, today ?? new Date()),
    setView: (view) => setChoices((current) => ({ ...current, view })),
    nav: (dir) =>
      setChoices((current) => ({
        ...current,
        anchor: navigatedAnchor(current.view, current.anchor, dir),
      })),
    toggleSubregion: (label) =>
      setChoices((current) => ({
        ...current,
        hidden: [...toggleHiddenLabel(new Set(current.hidden), label)],
      })),
    setHidden: (hidden) => setChoices((current) => ({ ...current, hidden })),
    toggleCancelled: () =>
      setChoices((current) => ({ ...current, showCancelled: !current.showCancelled })),
  };
}
